import { AxiosError } from 'axios';

export const MUTED_ERROR_MESSAGES = [
  'Network Error',
  'Network request failed',
  'User does not exist',
  'Incorrect username or password',
  'Failed to fetch',
  'Export does not belong to company'
];

export const MUTED_ERRORS_NAMES = ['UserNotFoundException'];

/**
 * Errors that are only muted when they originate from a specific source file
 * in the stacktrace. Use this (instead of MUTED_ERROR_MESSAGES) when the same
 * message can legitimately occur elsewhere and we only want to silence it for a
 * known third-party offender.
 */
export const MUTED_ERRORS_BY_STACK: {
  message: string;
  stackIncludes: string;
}[] = [
  // ag-grid mutates the DOM directly, which occasionally conflicts with React's
  // reconciliation during commit. Non-reproducible and non-crashing (confirmed
  // via Sentry replays). See PULSAR-WEB-FRONTEND-43 / FE-618.
  {
    message: "Failed to execute 'removeChild' on 'Node'",
    stackIncludes: 'ag-grid'
  },
  // Amplify v4 getUserData: whenever the access token carries the
  // `aws.cognito.signin.user.admin` scope, currentSession()/currentAuthenticatedUser()
  // call CognitoUser.getUserData. On a malformed response its success handler reads
  // `data.UserAttributes.length` and throws inside an un-awaited internal promise, so it
  // escapes every .catch as a global unhandled rejection. Benign — the session/token was
  // already retrieved and we never consume those attributes. Root cause is in the vendored
  // SDK; suppress the noise here. See PULSAR-WEB-FRONTEND-14W / FE-652.
  //
  // `stackIncludes: 'aws'` matches the throwing frame in both builds: dev paths contain
  // `@aws-amplify/...` and the prod build bundles Amplify into the `aws` chunk
  // (`static/js/aws-<hash>.js`, see vite.config manualChunks). We match the chunk, not the
  // `getUserData` frame name, because esbuild strips function names in prod (same reason
  // the ag-grid rule above matches its chunk).
  {
    message: 'length',
    stackIncludes: 'aws'
  }
];

export const THROTTLED_ERROR_CODES = [401, 403];
export const THROTTLING_RATE = 0.1;

export const isAxiosError = (error: unknown): error is AxiosError => {
  return (
    typeof error === 'object' &&
    error !== null &&
    'isAxiosError' in error &&
    (error as { isAxiosError?: unknown }).isAxiosError === true
  );
};

const isMutedByStack = (error: Error) => {
  return MUTED_ERRORS_BY_STACK.some(
    ({ message, stackIncludes }) =>
      error.message?.toLowerCase().includes(message.toLowerCase()) &&
      error.stack?.toLowerCase().includes(stackIncludes.toLowerCase())
  );
};

export const shouldLogErrorInSentry = (error: Error) => {
  return (
    !MUTED_ERROR_MESSAGES.some((mutedError) =>
      error.message?.toLowerCase().includes(mutedError.toLowerCase())
    ) &&
    !MUTED_ERRORS_NAMES.includes(error.name) &&
    !isMutedByStack(error)
  );
};

export const shouldThrottleError = (error: Error | AxiosError) => {
  if (isAxiosError(error)) {
    return (
      THROTTLED_ERROR_CODES.includes(error.response?.status ?? 0) &&
      Math.random() < THROTTLING_RATE
    );
  }
  return false;
};

type ErrorDetails = {
  message?: string;
};

type ErrorData = {
  message?: string;
  code?: string;
  details?: ErrorDetails;
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null;
};

const parseErrorData = (data: unknown): ErrorData | undefined => {
  if (!isRecord(data)) {
    return undefined;
  }

  const message = typeof data.message === 'string' ? data.message : undefined;
  const code = typeof data.code === 'string' ? data.code : undefined;
  const details = isRecord(data.details)
    ? {
        message:
          typeof data.details.message === 'string'
            ? data.details.message
            : undefined
      }
    : undefined;

  return {
    message,
    code,
    details
  };
};

export const getAxiosErrorData = (error: AxiosError): ErrorData | undefined => {
  return parseErrorData(error.response?.data);
};

export const getAxiosErrorMessage = (
  error: AxiosError | Error
): string | undefined => {
  if (isAxiosError(error)) {
    return getAxiosErrorData(error)?.message ?? error.message;
  }
  return error.message;
};

export const getAxiosErrorCode = (error: AxiosError): string | undefined => {
  const errorData = getAxiosErrorData(error);
  return errorData?.details?.message ?? errorData?.code;
};

export const getErrorMessage = (
  error: AxiosError | Error
): string | undefined => {
  return getAxiosErrorMessage(error);
};

// The backend sends no error code for this case; it is the only 403 that lists the affected `details.ids`.
export const isProductionAdvancementLockedError = (error: unknown): boolean => {
  if (!isAxiosError(error) || error.response?.status !== 403) {
    return false;
  }
  const data = error.response.data;
  return (
    isRecord(data) && isRecord(data.details) && Array.isArray(data.details.ids)
  );
};
